import { useEffect, useState } from "react";
import { useApi, useApiQuery } from "@/api/useApi";
import type { DepartmentCodes, ExpensePolicy, SecretStatus } from "@/api/types";
import { shortDate } from "@/format";
import type { RegisterPart, SaveAll } from "./settingsSave";

type Result = { ok: boolean; text: string } | undefined;

const message = (caught: unknown) => (caught instanceof Error ? caught.message : String(caught));

/** A write-only password field: says whether one is set, and replaces it when typed. */
function PasswordField({
  label,
  status,
  value,
  onChange,
}: {
  label: string;
  status: SecretStatus | undefined;
  value: string;
  onChange: (next: string) => void;
}) {
  if (status?.editable === false) {
    return <p className="muted">This environment takes the password from its configuration.</p>;
  }
  return (
    <label>
      {label}
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={status?.configured ? "Set · type to replace" : "Not set"}
        autoComplete="new-password"
        spellCheck={false}
      />
    </label>
  );
}

/**
 * Where paid reports are archived: a Windows share like \\PA-NAS\Expenses\{year}, written as a
 * service account, with a button that checks it works.
 */
export function ArchiveCard({
  policy,
  register,
  saveAll,
}: {
  policy: ExpensePolicy;
  register: RegisterPart;
  saveAll: SaveAll;
}) {
  const api = useApi();
  const secret = useApiQuery<SecretStatus>("/api/expenses/settings/secrets/archive");
  const [folder, setFolder] = useState(policy.archiveFolder ?? "");
  const [account, setAccount] = useState(policy.archiveUserName ?? "");
  const [delivery, setDelivery] = useState(policy.archiveDelivery ?? "folder");
  const [emailTo, setEmailTo] = useState((policy.archiveEmailTo ?? []).join(", "));
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<Result>();
  const [testing, setTesting] = useState(false);
  const isShare = folder.startsWith("\\\\") || folder.startsWith("//");
  const toFolder = delivery !== "email";
  const byEmail = delivery !== "folder";

  useEffect(() => {
    register("archive", {
      fields: () => ({
        archiveFolder: folder,
        archiveUserName: account,
        archiveDelivery: delivery,
        archiveEmailTo: emailTo
          .split(/[,;\s]+/)
          .map((address) => address.trim())
          .filter(Boolean),
      }),
      after: async () => {
        if (password === "") return;
        await api.put("/api/expenses/settings/secrets/archive", { password });
        setPassword("");
        secret.reload();
      },
    });
    return () => register("archive", undefined);
  });

  return (
    <section className="card stack">
      <h2>Archive</h2>
      <p className="muted">
        Each paid report is saved as a zip, named like "2026-10-17 Smith Jane.zip", with the report
        as a PDF and its receipts.
      </p>
      <label>
        Deliver archives
        <select
          value={delivery}
          onChange={(e) => setDelivery(e.target.value as "folder" | "email" | "both")}
        >
          <option value="folder">Save to a folder</option>
          <option value="email">Email them</option>
          <option value="both">Save and email</option>
        </select>
      </label>
      {byEmail && (
        <label>
          Email archives to
          <input
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="payroll@planetart.com"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </label>
      )}
      {byEmail && (
        <p className="muted">
          Sent from the expenses mailbox, one email per paid report with its zip attached. Addresses
          are separated by commas.
        </p>
      )}
      {toFolder && (
        <label>
          Save paid reports to
          <input
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            placeholder="\\PA-NAS\Expenses\{year}"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </label>
      )}
      {toFolder && (
        <p className="muted">
          {"{year}"} becomes the year payroll approved the report for payment.
        </p>
      )}
      {!toFolder ? null : isShare ? (
        <>
          <label>
            Account that saves there
            <input
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="US\svc-expenses"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <PasswordField
            label="Its password"
            status={secret.data}
            value={password}
            onChange={setPassword}
          />
          <p className="muted">
            A Windows share, reached over the office VPN. The account needs Modify on the share and
            folder.
          </p>
        </>
      ) : (
        <p className="muted">A folder in the Azure expense archive file share.</p>
      )}
      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
      {(isShare || byEmail) && (
        <button
          type="button"
          className="btn secondary"
          disabled={testing}
          onClick={() => {
            setTesting(true);
            setResult(undefined);
            saveAll()
              .then(() => api.post<{ detail: string }>("/api/expenses/settings/archive/test"))
              .then((tested) => setResult({ ok: true, text: tested.detail }))
              .catch((caught: unknown) => setResult({ ok: false, text: message(caught) }))
              .finally(() => setTesting(false));
          }}
        >
          {testing ? "Testing…" : "Save and test"}
        </button>
      )}
    </section>
  );
}

/**
 * Department codes for the GL report, read nightly from the office's SQL Server: where to find
 * them, the codes found, and the directory's departments that have none.
 */
export function DepartmentCodesCard({
  policy,
  register,
  saveAll,
  onSaved,
}: {
  policy: ExpensePolicy;
  register: RegisterPart;
  saveAll: SaveAll;
  onSaved: () => void;
}) {
  const api = useApi();
  const codes = useApiQuery<DepartmentCodes>("/api/expenses/settings/department-codes");
  const secret = useApiQuery<SecretStatus>("/api/expenses/settings/secrets/department-codes");
  const source = policy.departmentCodes;
  const [server, setServer] = useState(source?.server ?? "");
  const [database, setDatabase] = useState(source?.database ?? "");
  const [userName, setUserName] = useState(source?.userName ?? "");
  const [query, setQuery] = useState(source?.query ?? "");
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<Result>();
  const [busy, setBusy] = useState<"sync" | "upload">();
  const [showCodes, setShowCodes] = useState(false);
  const [from, setFrom] = useState<"sql" | "csv">(policy.departmentCodesSource ?? "sql");
  const data = codes.data;

  function upload(file: File) {
    const form = new FormData();
    form.append("file", file);
    setBusy("upload");
    setResult(undefined);
    api
      .postForm<DepartmentCodes>("/api/expenses/settings/department-codes/upload", form)
      .then((loaded) => {
        setResult({
          ok: true,
          text: `Loaded ${file.name}: ${String(loaded.peopleLoaded ?? 0)} people in ${String(loaded.codes.length)} departments.`,
        });
        codes.reload();
        onSaved();
      })
      .catch((caught: unknown) => {
        setResult({ ok: false, text: message(caught) });
        codes.reload();
      })
      .finally(() => setBusy(undefined));
  }

  useEffect(() => {
    register("departmentCodes", {
      fields: () => ({ departmentCodes: { server, database, userName, query } }),
      after: async () => {
        if (password === "") return;
        await api.put("/api/expenses/settings/secrets/department-codes", { password });
        setPassword("");
        secret.reload();
      },
    });
    return () => register("departmentCodes", undefined);
  });

  function readNow() {
    setBusy("sync");
    setResult(undefined);
    saveAll()
      .then(() => api.post<DepartmentCodes>("/api/expenses/settings/department-codes/sync"))
      .then((synced) => {
        setResult({ ok: true, text: `Found ${String(synced.codes.length)} department codes.` });
        codes.reload();
      })
      .catch((caught: unknown) => {
        setResult({ ok: false, text: message(caught) });
        codes.reload();
      })
      .finally(() => setBusy(undefined));
  }

  return (
    <section className="card stack">
      <h2>Department codes</h2>
      <p className="muted">
        Reports and the GL report show each person's department with its code, like "R&amp;D MTP -
        30720". People are matched to the office directory by employee number, then by email, then
        by their Entra department.
      </p>
      <label>
        Read the directory from
        <select value={from} onChange={(e) => setFrom(e.target.value as "sql" | "csv")}>
          <option value="sql">SQL Server, nightly</option>
          <option value="csv">An uploaded CSV</option>
        </select>
      </label>
      {from === "csv" && (
        <>
          <label>
            Employee CSV
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={busy !== undefined}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) upload(file);
              }}
            />
          </label>
          <p className="muted">
            A header row with EmployeeNumber, Email, Department and DepartmentNumber columns, one
            row per person. Uploading replaces the last file
            {policy.departmentCodesFileName && policy.departmentCodesSource === "csv"
              ? ` (${policy.departmentCodesFileName})`
              : ""}
            .
          </p>
        </>
      )}
      {from === "sql" && (
        <>
          <div className="split">
            <label>
              SQL Server
              <input
                value={server}
                onChange={(e) => setServer(e.target.value)}
                placeholder="cpa-it-sql-01"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <label>
              Database
              <input
                value={database}
                onChange={(e) => setDatabase(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          </div>
          <div className="split">
            <label>
              Read-only login
              <input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <PasswordField
              label="Its password"
              status={secret.data}
              value={password}
              onChange={setPassword}
            />
          </div>
          <label>
            Query
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SELECT EmployeeNumber, DepartmentNumber, Department FROM dbo.Directory"
              rows={3}
              spellCheck={false}
            />
          </label>
          <p className="muted">
            Needs DepartmentNumber and Department columns, plus EmployeeNumber and Email to match
            people. If someone is listed twice, their first row is used.
          </p>
        </>
      )}
      {data && (
        <dl className="facts">
          <div>
            <dt>Last read</dt>
            <dd>
              {data.syncedAt ? shortDate(data.syncedAt.slice(0, 10)) : "Never"}
              {data.codes.length > 0 &&
                ` · ${String(data.peopleLoaded ?? 0)} people in ${String(data.codes.length)} departments`}
            </dd>
          </div>
          {data.syncError && (
            <div>
              <dt>Last attempt</dt>
              <dd className="danger-text">{data.syncError}</dd>
            </div>
          )}
          {data.unmatched.length > 0 && (
            <div>
              <dt>No code for</dt>
              <dd>{data.unmatched.join(", ")}</dd>
            </div>
          )}
        </dl>
      )}
      {data && data.codes.length > 0 && (
        <>
          <button type="button" className="btn ghost" onClick={() => setShowCodes(!showCodes)}>
            {showCodes ? "Hide codes" : "Show codes"}
          </button>
          {showCodes && (
            <table className="gl-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Department</th>
                  <th>People</th>
                </tr>
              </thead>
              <tbody>
                {data.codes.map((code) => (
                  <tr key={code.code + code.name}>
                    <td>{code.code}</td>
                    <td>{code.name}</td>
                    <td>{code.people}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
      {busy === "upload" && <p className="muted">Loading the file…</p>}
      {from === "sql" && (
        <button
          type="button"
          className="btn secondary"
          disabled={busy !== undefined || server.trim() === ""}
          onClick={readNow}
        >
          {busy === "sync" ? "Reading codes…" : "Save and read codes now"}
        </button>
      )}
    </section>
  );
}
